import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { isStandalone } from "@/lib/install";

const KEY = "jaypanduh_splash_shown";

const shouldShow = () => {
  const forced = new URLSearchParams(window.location.search).has("splash");
  let seen = false;
  try { seen = sessionStorage.getItem(KEY) === "1"; } catch { /* ignore */ }
  return forced || (isStandalone() && !seen);
};

export const SplashScreen = () => {
  const [show, setShow] = useState(shouldShow);
  useEffect(() => {
    if (!show) return undefined;
    try { sessionStorage.setItem(KEY, "1"); } catch { /* ignore */ }
    const t = setTimeout(() => setShow(false), 2700);
    return () => clearTimeout(t);
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="splash-screen"
          onClick={() => setShow(false)}
          className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center overflow-hidden"
          initial={{ clipPath: "circle(150% at 50% 42%)" }}
          animate={{ clipPath: "circle(150% at 50% 42%)" }}
          exit={{ clipPath: "circle(0% at 50% 42%)" }}
          transition={{ duration: 0.65, ease: [0.7, 0, 0.3, 1] }}
        >
          {[0.55, 1.0, 1.45, 1.9].map((d) => (
            <motion.span
              key={d}
              className="absolute top-[42%] left-1/2 w-64 h-64 -ml-32 -mt-32 rounded-full border-2 border-[#56ED4A]"
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: [0.4, 3.2], opacity: [0.9, 0] }}
              transition={{ delay: d, duration: 1.1, ease: "easeOut" }}
            />
          ))}
          <motion.div
            className="absolute top-[42%] left-1/2 w-80 h-80 -ml-40 -mt-40 rounded-full bg-[#56ED4A] blur-[90px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.35, 0.15, 0.35, 0.15, 0.35] }}
            transition={{ delay: 0.5, duration: 2 }}
          />
          <motion.div
            className="relative w-52 h-52 sm:w-60 sm:h-60"
            initial={{ y: "-110vh", rotate: -25 }}
            animate={{ y: 0, rotate: 0 }}
            transition={{ type: "spring", stiffness: 140, damping: 11, mass: 0.9 }}
          >
            <motion.div
              className="relative w-full h-full"
              animate={{ scale: [1, 1, 1.14, 1, 1.14, 1, 1.14, 1], rotate: [0, 0, -7, 0, 7, 0, -4, 0] }}
              transition={{ duration: 2.1, times: [0, 0.3, 0.4, 0.5, 0.62, 0.72, 0.84, 1], ease: "easeInOut" }}
            >
              <img src="/icon-v2-512.png" alt="JayPanduh" className="absolute inset-0 w-full h-full object-contain rounded-full" />
              <motion.img
                src="/icon-v2-512.png" alt="" aria-hidden="true"
                className="absolute inset-0 w-full h-full object-contain rounded-full mix-blend-screen"
                style={{ filter: "sepia(1) hue-rotate(60deg) saturate(6)" }}
                animate={{ x: [0, 0, -10, 6, 0, 0, 8, -6, 0], opacity: [0, 0, 0.8, 0.6, 0, 0, 0.8, 0.5, 0] }}
                transition={{ duration: 2.1, times: [0, 0.38, 0.41, 0.44, 0.47, 0.8, 0.83, 0.86, 0.9] }}
              />
            </motion.div>
          </motion.div>
          <div className="mt-6 flex items-end gap-1.5 h-8" aria-hidden="true">
            {[0, 1, 2, 3, 4, 5, 6].map((b) => (
              <motion.span
                key={b}
                className="w-1.5 bg-[#56ED4A]"
                initial={{ height: 4 }}
                animate={{ height: [4, 28, 8, 22, 6, 30, 4] }}
                transition={{ delay: 0.6 + b * 0.05, duration: 1.6, repeat: 1, ease: "easeInOut" }}
              />
            ))}
          </div>
          <div className="overflow-hidden mt-3">
            <motion.p
              className="font-display uppercase text-5xl tracking-wide chrome-text"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ delay: 0.85, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              JayPanduh
            </motion.p>
          </div>
          <motion.p
            className="mt-2 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
          >
            Tap to skip
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
